import React from "react";
import { FormattedMessage } from "react-intl";

import { featureFlags } from "~/common/feature-flags";
import FileUpload from "~/components/file_upload";
import { Label, LabelRequiredIndicator } from "~/components/ui/label";
import { PhotoClassifierEntry } from "~/features/photo-classifier/photo-classifier-entry";
import type { TPatient } from "~/types/patient";

import { PhotoProtocolGrid, SLOT_LABEL_DOM_ID } from "./photo-protocol-layout";

interface PatientNewImagesProps {
  required?: boolean;
  displayHeading?: boolean;
  correctionNumber?: number;
  patient_id?: TPatient["patient_id"];
  /** Overrides the FileUpload slots' default teal progress bar — e.g. power cap's dark #524B46. */
  progressBarColor?: string;
  /** See `FileUpload`'s own doc — skips the "existing photo" preload for a brand-new, non-correction order. */
  ignoreExistingMedia?: boolean;
}

export function PatientNewImages({
  required = true,
  displayHeading = true,
  correctionNumber,
  patient_id = 0,
  progressBarColor,
  ignoreExistingMedia,
}: PatientNewImagesProps) {
  const heading = displayHeading ? (
    <Label id="validation-course_id" size="xl" readOnly>
      <FormattedMessage id="UPLOAD_FHOTOS" />
      {required ? <LabelRequiredIndicator /> : null}
    </Label>
  ) : null;

  return (
    <div
      data-matomo-mask
      data-hj-suppress
      className="form-body tw-relative"
      id="photo-protocol-block"
    >
      {/*
        Feature-flagged (`featureFlags.photoClassifier`, default OFF — still off in production):
        when enabled, a capable browser gets the in-browser AI classifier (single multi-image drop
        area) that on confirm fills the redux media the legacy slots read, so submit/validation is
        unchanged; a browser or deployment that cannot run it gets the legacy 8-slot `fallback` plus
        a notice saying why — never both grids. When the flag is OFF nothing of the classifier runs:
        no capability probe, no preflight request, no notice, no chunk — this branch renders the
        legacy grid exactly as it did before the feature existed (docs/features/photo-classifier.md).
      */}
      {featureFlags.photoClassifier ? (
        <PhotoClassifierEntry
          patientId={patient_id}
          correctionNumber={correctionNumber}
          required={required}
          heading={heading}
          fallback={
            <LegacyPhotoProtocol
              patient_id={patient_id}
              correctionNumber={correctionNumber}
              progressBarColor={progressBarColor}
              ignoreExistingMedia={ignoreExistingMedia}
              required={required}
            />
          }
        />
      ) : (
        <>
          {heading}
          <LegacyPhotoProtocol
            patient_id={patient_id}
            correctionNumber={correctionNumber}
            progressBarColor={progressBarColor}
            ignoreExistingMedia={ignoreExistingMedia}
            required={required}
          />
        </>
      )}
    </div>
  );
}

/**
 * The original one-image-per-view upload grid — one `FileUpload` per protocol slot, laid out by the
 * shared `PhotoProtocolGrid`. Used as-is for unsupported/degraded browsers, and as the manual
 * alternative the doctor can switch back to.
 */
function LegacyPhotoProtocol({
  patient_id,
  correctionNumber,
  progressBarColor,
  ignoreExistingMedia,
  required,
}: {
  patient_id: TPatient["patient_id"];
  correctionNumber?: number;
  progressBarColor?: string;
  ignoreExistingMedia?: boolean;
  required: boolean;
}) {
  return (
    <PhotoProtocolGrid
      variant="legacy"
      required={required}
      testId="legacy-photo-protocol"
      renderSlot={(view) => (
        <FileUpload
          preview
          id={view}
          semantics={view}
          patient_id={patient_id}
          correctionNum={correctionNumber}
          ariaLabelledBy={SLOT_LABEL_DOM_ID[view]}
          progressBarColor={progressBarColor}
          ignoreExistingMedia={ignoreExistingMedia}
        />
      )}
    />
  );
}
